import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Tab } from '@headlessui/react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { InquiryForm } from '../types';
import { useInquiryStore } from '../store/inquiryStore';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';
import api from '../config/axios';

const Services = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { addInquiry } = useInquiryStore();
  const { register, handleSubmit, reset, formState: { errors } } = useForm<InquiryForm>();

  const onSubmit = async (data: InquiryForm) => {
    try {
      await api.post('/api/inquiries', data);
      await addInquiry(data);
      toast.success('Wave Incoming—We\'ll Reply in 24h!');
      reset();
      setIsOpen(false);
    } catch (error) {
      toast.error('Failed to send inquiry. Please try again.');
    }
  };

  return (
    <>
      <Helmet>
        <title>AlonForge Solutions | Tailored UI & Systems</title>
        <meta
          name="description"
          content="From static portfolios to dynamic dashboards—pick your tier"
        />
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-br from-navy to-skyblue py-20 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 font-heading">
            Tailored UI & Systems for Your Hustle
          </h1>
          <p className="text-xl max-w-2xl mx-auto">
            From static portfolios to dynamic dashboards—pick your tier.
          </p>
        </div>
      </section>

      <div className="min-h-screen bg-slate py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tab.Group>
            <Tab.List className="flex space-x-1 rounded-xl bg-blue-900/20 p-1 mb-8">
              <Tab className="w-full rounded-lg py-2.5 text-sm font-medium leading-5 text-navy ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2 ui-selected:bg-white ui-selected:shadow ui-not-selected:text-blue-100 ui-not-selected:hover:bg-white/[0.12] ui-not-selected:hover:text-white">
                Portfolio Websites
              </Tab>
              <Tab className="w-full rounded-lg py-2.5 text-sm font-medium leading-5 text-navy ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2 ui-selected:bg-white ui-selected:shadow ui-not-selected:text-blue-100 ui-not-selected:hover:bg-white/[0.12] ui-not-selected:hover:text-white">
                Systems Add-Ons
              </Tab>
            </Tab.List>

            <Tab.Panels>
              {/* Portfolio Websites Tab */}
              <Tab.Panel>
                <div>
                  <h2 className="text-2xl font-bold mb-8 text-center font-heading">Portfolio Website Tiers</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Basic UI Card */}
                    <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105 border-2 border-gray-200">
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-navy mb-2 font-heading">Basic UI</h3>
                        <div className="text-3xl font-bold text-skyblue mb-1">₱2,900</div>
                        <p className="text-sm text-gray-500">One-time payment</p>
                      </div>
                      <ul className="space-y-3 mb-6">
                        <li className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-skyblue mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">1 Static Page</span>
                        </li>
                        <li className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-skyblue mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">Responsive Design</span>
                        </li>
                        <li className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-skyblue mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">Contact Form</span>
                        </li>
                        <li className="flex items-start text-gray-400">
                          <span className="mr-2">✗</span>
                          <span className="text-sm">Custom Domain</span>
                        </li>
                        <li className="flex items-start text-gray-400">
                          <span className="mr-2">✗</span>
                          <span className="text-sm">SEO Optimization</span>
                        </li>
                        <li className="flex items-start text-gray-400">
                          <span className="mr-2">✗</span>
                          <span className="text-sm">Blog Integration</span>
                        </li>
                      </ul>
                      <button
                        onClick={() => setIsOpen(true)}
                        className="w-full bg-skyblue text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors font-semibold"
                      >
                        Get Started
                      </button>
                    </div>

                    {/* Pro UI Card */}
                    <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105 border-2 border-skyblue relative">
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-skyblue text-white px-3 py-1 rounded-full text-xs font-semibold">
                          Popular
                        </span>
                      </div>
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-navy mb-2 font-heading">Pro UI</h3>
                        <div className="text-3xl font-bold text-skyblue mb-1">₱5,900</div>
                        <p className="text-sm text-gray-500">One-time payment</p>
                      </div>
                      <ul className="space-y-3 mb-6">
                        <li className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-skyblue mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">Multi Pages w/ CMS</span>
                        </li>
                        <li className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-skyblue mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">Responsive Design</span>
                        </li>
                        <li className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-skyblue mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">Custom Domain</span>
                        </li>
                        <li className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-skyblue mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">SEO Optimization</span>
                        </li>
                        <li className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-skyblue mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">Contact Form</span>
                        </li>
                        <li className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-skyblue mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">Analytics Setup</span>
                        </li>
                        <li className="flex items-start text-gray-400">
                          <span className="mr-2">✗</span>
                          <span className="text-sm">Blog Integration</span>
                        </li>
                      </ul>
                      <button
                        onClick={() => setIsOpen(true)}
                        className="w-full bg-navy text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors font-semibold"
                      >
                        Get Started
                      </button>
                    </div>

                    {/* Premium System Card */}
                    <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105 border-2 border-navy">
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-navy mb-2 font-heading">Premium System</h3>
                        <div className="text-3xl font-bold text-navy mb-1">₱9,900</div>
                        <p className="text-sm text-gray-500">One-time payment</p>
                      </div>
                      <ul className="space-y-3 mb-6">
                        <li className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-skyblue mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">Full Custom CRM</span>
                        </li>
                        <li className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-skyblue mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">Responsive Design</span>
                        </li>
                        <li className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-skyblue mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">Custom Domain</span>
                        </li>
                        <li className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-skyblue mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">SEO Optimization</span>
                        </li>
                        <li className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-skyblue mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">Contact Form</span>
                        </li>
                        <li className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-skyblue mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">Blog Integration</span>
                        </li>
                        <li className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-skyblue mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">Analytics Setup</span>
                        </li>
                      </ul>
                      <button
                        onClick={() => setIsOpen(true)}
                        className="w-full bg-navy text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors font-semibold"
                      >
                        Get Started
                      </button>
                    </div>

                    {/* Customized Pricing Card */}
                    <div className="bg-gradient-to-br from-navy to-skyblue rounded-xl shadow-md p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-white">
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold mb-2 font-heading">Customized</h3>
                        <div className="text-3xl font-bold mb-1">Custom</div>
                        <p className="text-sm text-blue-100">Tailored to your needs</p>
                      </div>
                      <ul className="space-y-3 mb-6">
                        <li className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-white mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">Fully Custom Solution</span>
                        </li>
                        <li className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-white mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">Unlimited Features</span>
                        </li>
                        <li className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-white mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">Dedicated Support</span>
                        </li>
                        <li className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-white mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">Priority Development</span>
                        </li>
                        <li className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-white mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">Ongoing Maintenance</span>
                        </li>
                        <li className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-white mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">Custom Integrations</span>
                        </li>
                      </ul>
                      <button
                        onClick={() => setIsOpen(true)}
                        className="w-full bg-white text-navy px-4 py-2 rounded-lg hover:bg-slate transition-colors font-semibold"
                      >
                        Request Quote
                      </button>
                    </div>
                  </div>
                </div>
              </Tab.Panel>

              {/* Systems Add-Ons Tab */}
              <Tab.Panel>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                    <span className="bg-skyblue text-white px-3 py-1 rounded-full text-sm font-semibold">
                      ₱15,000
                    </span>
                    <h3 className="text-2xl font-bold mt-4 mb-2 font-heading">Freelancer CRM</h3>
                    <p className="text-gray-600 mb-4">
                      Track gigs & clients. Perfect for virtual assistants managing multiple clients and projects.
                    </p>
                    <Link
                      to="/systems"
                      className="text-skyblue hover:text-navy font-semibold"
                    >
                      Learn more →
                    </Link>
                  </div>

                  <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                    <span className="bg-skyblue text-white px-3 py-1 rounded-full text-sm font-semibold">
                      ₱12,000
                    </span>
                    <h3 className="text-2xl font-bold mt-4 mb-2 font-heading">Inventory Tracker</h3>
                    <p className="text-gray-600 mb-4">
                      Real-time stock for shops. Keep track of inventory levels, sales, and reorder points.
                    </p>
                    <Link
                      to="/systems"
                      className="text-skyblue hover:text-navy font-semibold"
                    >
                      Learn more →
                    </Link>
                  </div>

                  <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                    <span className="bg-skyblue text-white px-3 py-1 rounded-full text-sm font-semibold">
                      From ₱10,000
                    </span>
                    <h3 className="text-2xl font-bold mt-4 mb-2 font-heading">Custom Automations</h3>
                    <p className="text-gray-600 mb-4">
                      Zapier workflows, automated email sequences, and task management integrations.
                    </p>
                    <Link
                      to="/systems"
                      className="text-skyblue hover:text-navy font-semibold"
                    >
                      Learn more →
                    </Link>
                  </div>
                </div>
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </div>
      </div>

      {/* Consultation Modal */}
      <Dialog open={isOpen} onClose={() => setIsOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-sm rounded bg-white p-6">
            <div className="flex justify-between items-center mb-4">
              <Dialog.Title className="text-xl font-bold font-heading">Book Consultation</Dialog.Title>
              <button onClick={() => setIsOpen(false)}>
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  {...register('name', { required: 'Name is required' })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  {...register('email', { required: 'Email is required' })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Interest</label>
                <select
                  {...register('service')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Select interest</option>
                  <option value="UI Portfolio">UI Portfolio</option>
                  <option value="IT System">IT System</option>
                  <option value="Both">Both</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Message</label>
                <textarea
                  {...register('message', { required: 'Message is required' })}
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
                {errors.message && (
                  <p className="text-red-500 text-sm mt-1">{errors.message.message}</p>
                )}
              </div>
              <button
                type="submit"
                className="w-full bg-navy text-white px-6 py-3 rounded-lg hover:bg-blue-800 transition-colors font-semibold"
              >
                Submit
              </button>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  );
};

export default Services;
